/**
 * Help section copy (Simplified Chinese). Same keys as helpSections.en.ts
 */
import type { HelpSectionMap } from './helpSections.en';

const helpSectionsZhCN: HelpSectionMap = {
  'getting-started': {
    intro:
      '开始所需的内容都在这里。按步骤操作，或从侧边栏浏览。',
    stepsTitle: '第一步',
    step1: '打开 **概览** 查看仪表板和最近活动。',
    step2: '打开 **我的任务** 查看分配给您的所有工作项。',
    step3: '在侧边栏点击 **项目** 打开看板、待办列表、路线图等。',
    stepPm:
      '打开 **项目管理** 可查看所有项目及路线图概要（当前阶段、下次部署、计划日期）。',
    step4: '使用顶栏 **创建** 按钮添加新工作项。',
    step5:
      '使用顶栏 **主题切换**（太阳/月亮图标）在浅色与深色模式间切换。',
    outro:
      '使用左侧链接跳转到各主题，或进行上方的快速导览。',
  },
  notifications: {
    intro:
      '顶栏铃铛显示应用内通知。点击打开通知中心并标记为已读。',
    whatTitle: '您会收到哪些通知',
    n1: '有人将工作项分配给您时',
    n2: '在评论中被 **@提及** 时',
    n3: '状态变更、评论和截止日期提醒',
    n4: '项目邀请（若组织启用）',
    prefsTitle: '管理偏好',
    prefsBody:
      '在个人资料或账户设置中选择邮件与应用内通知类型（例如仅分配，或包含提及）。',
    sessionsBody:
      '在个人资料中还可查看 **活动会话** 并在其他设备退出登录。',
    tip: '打开铃铛，点击「全部标为已读」可清空列表。',
  },
  'keyboard-shortcuts': {
    intro:
      '在主内容获得焦点时使用。Mac 用 **Cmd**，Windows/Linux 用 **Ctrl**。',
    k1: '**Cmd/Ctrl + K** 或 **/** – 搜索工作项与项目（聚焦搜索）。工作项弹窗打开时不可用。',
    k2: '**Cmd/Ctrl + I** – 打开创建工作项',
    k3: '**Cmd/Ctrl + Enter** – 提交创建工作项表单',
    k4: '**Cmd/Ctrl + S** – 保存（文档编辑器）',
    k5: '**Escape** – 关闭弹窗或搜索',
    tip: '按 / 或 Cmd/Ctrl + K 可在不离开页面的情况下搜索。列表视图对长列表使用虚拟滚动。',
  },
  projects: {
    intro:
      '项目包含工作项、冲刺和报表。您可从侧边栏打开所参与的任何项目。',
    createTitle: '如何创建项目',
    createBody:
      '进入 **项目管理** 并点击 **创建项目**。填写名称、描述和类别。可使用模板预填工作流与设置。',
    membersTitle: '管理成员',
    membersLead:
      '打开 **项目管理** 可查看所有项目、导出 CSV 及 **路线图概要**（各项目当前阶段、下次部署、计划日期）。打开项目后使用 **管理成员**：',
    m1: '添加人员并设置角色（管理员、成员或查看者）。',
    m2:
      '添加 **群组**（管理设置 → 群组），使群内成员获得相同角色。',
    m3: '更改角色或移除成员。',
    rolesHint:
      '查看者 = 只读。成员 = 编辑工作项与评论。管理员 = 管理成员与设置。每个项目至少需一名管理员。',
    notePlan:
      '项目创建可能受组织方案（Free、Pro、Enterprise）限制。见下文「方案与限制」。',
    memberOnly:
      '仅管理员与项目经理可创建项目。若需要新项目，请联系管理员或使用申请访问流程。',
  },
  issues: {
    intro:
      '工作项是您跟踪的任务：任务、缺陷、故事、史诗与子任务。',
    createTitle: '如何创建工作项',
    c1: '点击顶栏 **创建**。',
    c2: '选择 **创建工作项** 或 **从 CSV 批量创建**。',
    c3: '填写表单（项目、标题、类型、截止日期等）并提交。',
    createTip: '也可从项目看板或待办列表创建工作项。',
    typesTitle: '工作项类型',
    typeTask: '**任务** – 一般工作',
    typeStory: '**故事** – 面向用户的功能',
    typeBug: '**缺陷** – 需要修复的问题',
    typeEpic: '**史诗** – 大型举措；其他工作项可链接',
    typeSub: '**子任务** – 隶属于另一工作项',
    fieldsTitle: '可设置的字段',
    fieldsBody:
      '标题、描述、负责人、优先级、状态和截止日期为基本项。还可添加标签、组件、版本、父级或史诗链接、时间估算和附件。创建时可添加 **关联工作项** 并选择链接类型（如：阻塞、被阻塞、相关、重复）；所选类型会作为下次链接的默认。打开工作项可编辑全部内容并添加评论。',
  },
  backlog: {
    intro:
      '查看未纳入冲刺的工作项并管理冲刺。拖动可重新排序待办列表或移入冲刺。',
    b1: '将工作项拖入冲刺以规划工作。',
    b2: '创建冲刺并在 已计划 → 进行中 → 已完成 间移动。',
    b3: '必要时将工作项移回待办列表。',
    b4:
      '大型待办列表使用虚拟滚动以提升性能；拖放排序逻辑与此前一致。',
  },
  'project-management': {
    intro:
      '面向管理员与项目经理。从侧边栏打开 **项目管理** 可查看所有项目及高层路线图概要。',
    allTitle: '所有项目',
    allBody:
      '表格列出组织中的每个项目。您可新建项目、导出 CSV，并打开各项目的看板、待办列表、路线图或文档。按项目使用 **管理成员** 添加人员并设置角色。',
    archivedBody:
      '开启 **显示已归档** 可包含已归档项目。组织管理员与项目管理员可在 **操作** 中归档或恢复；恢复前，已归档项目不会出现在默认列表和大多数选择器中。',
    roadmapTitle: '路线图概要',
    roadmapLead:
      '页面顶部的 **路线图概要** 为每个项目显示：',
    ro1: '**当前阶段** – 最新已发布版本（如有）。',
    ro2: '**下次部署** – 下一个计划版本的名称。',
    ro3: '**计划日期** – 该版本的发布日期（若已设置）。',
    roadmapFoot:
      '据此了解各项目进展与下次发布时间。点击项目名称或 **查看完整路线图** 打开跨项目或单项目路线图。',
    roadmapNote:
      '版本与发布日期按项目配置（管理设置或项目设置）。添加版本并在工作项上设置 **修复版本** 以填充路线图。',
  },
  roadmap: {
    intro:
      '路线图展示版本与发布，便于了解计划与时间。FlowBoard 提供单项目路线图与组织级跨项目路线图。',
    projTitle: '项目路线图',
    projLead: '打开项目并在侧边栏选择 **路线图**，您将看到：',
    pr1:
      '摘要卡片：**当前阶段**（最新发布版本）、**下次部署**（下一计划版本）、**计划日期**（发布日期）。',
    pr2:
      '**列表视图** – 含描述、开始/发布日期与工作项（修复版本 / 影响版本）的版本时间线。',
    pr3:
      '**时间线视图** – 版本以条形显示；在版本间拖动工作项标记可更改工作项的 **修复版本**。',
    fixBody:
      '在项目中（或组织级）添加版本，并为工作项设置 **修复版本** / 影响版本，使其出现在路线图上。',
    crossTitle: '跨项目路线图',
    crossBody:
      '从侧边栏打开 **跨项目路线图** 可在一个列表中查看所有项目的所有版本。每行显示项目、版本名、发布日期及工作项数量（修复 / 影响）。点击项目名称打开该项目的路线图。',
    tip: '用路线图与相关方对齐现状与下次部署计划。为版本设置开始与发布日期可获得更准确的时间线。',
  },
  calendar: {
    body: '有截止日期的工作项会显示在日历上。切换月份以规划或发现逾期工作。',
  },
  'list-view': {
    intro:
      '项目中所有工作项的表格。可排序、筛选，并批量编辑状态或负责人。',
    lv1: '使用高级筛选按状态、负责人、标签等缩小范围。',
    lv2: '多选工作项可一次性更改状态、负责人或删除。',
    lvBulk:
      '批量编辑可一次更新许多工作项。从项目列表还可设置标签、组件、修复版本和解决结果。',
    lv3:
      '列表较长时，使用底部的 **分页** 切换页面并控制每页条数。',
    lv4:
      '**已保存布局** 在本浏览器保存筛选、搜索、排序和列选择（与服务器端已保存筛选不同）。开启 **SLA** 列可查看处于 SLA 违约状态的工作项。',
    tip: '需要比较多项工作或批量更新时，列表视图最合适。',
  },
  'dependency-graph': {
    body: '以图形展示工作项如何链接（阻塞、被阻塞、相关），用于评估影响与关键路径。',
  },
  docs: {
    intro:
      '每个项目都有 **文档** 区域（类似 Confluence），用于规格、运维手册和会议纪要。打开项目后从侧边栏进入 **文档**。',
    tplTitle: '页面模板与标签',
    tplBody:
      '创建或编辑页面时可选择 **模板**（如会议纪要、规格）并添加 **标签**（如 spec、meeting-notes）以便整理与查找。',
    sidebarTitle: '左侧边栏',
    sidebarBody:
      '左侧显示所有页面的树形结构。使用 **新建页面** 创建，搜索框可按标题筛选。用箭头展开或折叠文件夹，点击标题打开页面。',
    pageTitle: '页面上',
    d1: '**本页目录** – 查看（非编辑）时根据 H2/H3 标题生成固定目录。',
    d2: '**子页面** – 若有子页面，在内容下方列出并附链接。',
    d3: '**编辑** – 修改标题、父页面与正文。工具栏支持格式、链接与图片。',
    d4: '**历史** – 查看并恢复历史版本。',
    d5: '**导出** – 将页面下载为 Markdown。',
    d6: '评论显示在底部，可添加与查看回复。',
    tip: '在文档编辑器中按 **Ctrl + S** 可在不离开页面的情况下保存。',
  },
  reports: {
    intro:
      '在项目中打开 **报表** 可查看速率、累积流、控制图、史诗进度、工时、报表构建器及 **工作流**（甘特）视图。需要分享时可导出数据或工作流图。',
    typesTitle: '报表类型',
    r1: '**速率** – 每个冲刺完成的工作量。',
    r2: '**累积流** – 工作项随时间在各状态间的流动。',
    r3: '**控制图** – 已完成工作项的周期时间。',
    r4:
      '**史诗报表** – 使用 **史诗** 下拉框筛选单个史诗或查看全部。可通过导出按钮导出史诗数据。',
    r5: '**工时跟踪** – 按类型和用户比较估算与实际耗时。',
    r6:
      '**工作流（甘特）** – 带截止日期的工作项时间线：层级（史诗→任务→子任务）、进度条、依赖线（父子与「阻塞」等链接）、里程碑（同日项显示为菱形）。支持缩放（日/周/月）及 **导出为图片** 保存为 PNG。条形颜色使用项目状态色。',
    r7: '**报表构建器** – 按状态、类型、优先级或负责人分组。',
    tip: '结合速率与累积流了解团队吞吐并发现瓶颈。',
  },
  dashboards: {
    body: '前往 **自定义仪表板**，用小组件（按项目、群组或负责人）搭建自己的仪表板。可按群组或角色控制谁可查看。',
  },
  'admin-settings': {
    intro:
      '从侧边栏打开 **管理设置** 以管理组织与项目配置。',
    orgTitle: '组织',
    orgBody: '编辑组织名称、管理成员并处理待处理邀请。',
    projCfgTitle: '项目配置',
    projCfgBody:
      '管理各项目共用的 **标签**、**组件**、**版本** 与 **自定义字段**。',
    tplTitle: '模板',
    tplBody:
      '**项目** 与 **工作项** 模板为新项目或工作项设置默认工作流与字段。',
    autoTitle: '自动化',
    autoBody:
      '当工作项满足条件（如状态变更、设置负责人）时触发的规则。用触发器与操作自动化重复工作。表格中的 **编辑**（铅笔）可改名称、描述、触发器或启用状态；**删除** 可移除规则。',
    groupsTitle: '群组',
    groupsBody:
      '创建群组并添加成员，再在 **管理成员** 中将群组分配到项目，使群内成员获得相同项目角色。',
    rolesTitle: '角色与审计日志',
    rolesBody: '管理自定义角色并查看重要变更的审计日志。',
    systemTitle: '系统（仅超级管理员）',
    systemBody:
      '**系统** 标签列出所有组织。可设置各组织的 **方案**（Free、Pro、Enterprise）并邀请新企业。',
  },
  glossary: {
    intro: 'FlowBoard 中常见术语的简要定义。',
    g1: '**工作项** – 任务、故事、缺陷、史诗或子任务。',
    g2: '**史诗** – 大型举措；其他工作项可链接到它。',
    g3: '**冲刺** – 固定时间段内的一组工作项（如 2 周）。',
    g4: '**待办列表** – 尚未进入冲刺的工作项。',
    g5: '**版本** – 发布或里程碑（如 v1.0）。可有开始与发布日期；可标记为已发布。',
    g6: '**修复版本** – 计划修复或交付工作项的版本。显示在路线图上。',
    g7: '**影响版本** – 工作项影响的版本（常用于缺陷）。亦显示在路线图上。',
    g8: '**看板** – 按状态分列；拖动卡片更新。',
    g9: '**工作流** – 状态与转换（如 待办 → 进行中 → 已完成）。',
    g10: '**负责人** – 对工作项负责的人员。',
    g11: '**关注者** – 接收更新通知的用户。',
  },
  'subscription-plans': {
    body: 'Free 方案限制项目与用户数量；Standard、Premium、Enterprise 允许更多（含不限量）。达到上限后需升级方案才能继续创建。超级管理员在 管理设置 → 系统 中设置各组织的方案。',
  },
  'api-integrations': {
    intro:
      'FlowBoard 提供面向工作项、项目、报表、Webhook 等的 REST API。使用实例的 API 基础 URL 与有效认证令牌（如登录后）发起请求。',
    webhooksTitle: 'Webhooks',
    webhooksBody:
      '组织管理员可在 **管理 → Webhooks** 配置出站 Webhook。在工作项与项目事件上发送 HTTP POST；可选 HMAC 密钥用于校验。要向 Slack 发送通知，可添加 Slack 传入 Webhook URL 的 Webhook（或使用自动化规则中的 Webhook 操作）。',
    openapiNote:
      '后端运行时可在 **/api/docs/openapi.json** 获取 OpenAPI 3.0 规范，可与 Swagger UI 等工具配合使用。',
  },
};

export default helpSectionsZhCN;
